/**
 * useGoogleMaps — Lazy-loads the Google Maps JS API once across the app.
 *
 * Returns { loaded, error }. Other components await this before rendering
 * Places/Distance widgets.
 */
import { useEffect, useState } from "react";

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const SCRIPT_ID = "google-maps-js";
let loadPromise = null;

const loadScript = () => {
  if (loadPromise) return loadPromise;
  loadPromise = new Promise((resolve, reject) => {
    if (typeof window === "undefined") return reject(new Error("no window"));
    if (window.google && window.google.maps && window.google.maps.places) {
      return resolve();
    }
    if (!API_KEY) return reject(new Error("Cheia Google Maps lipsește"));
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Script error")));
      return;
    }
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.async = true;
    s.defer = true;
    s.src = `https://maps.googleapis.com/maps/api/js?key=${API_KEY}&libraries=places,geometry,drawing&language=ro&region=RO`;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Script load failed"));
    document.head.appendChild(s);
  });
  return loadPromise;
};

export const useGoogleMaps = () => {
  const [loaded, setLoaded] = useState(
    typeof window !== "undefined" && !!(window.google && window.google.maps && window.google.maps.places)
  );
  const [error, setError] = useState(null);

  useEffect(() => {
    if (loaded) return;
    let cancelled = false;
    loadScript()
      .then(() => { if (!cancelled) setLoaded(true); })
      .catch((e) => { if (!cancelled) setError(e); });
    return () => { cancelled = true; };
  }, [loaded]);

  return { loaded, error };
};

/**
 * Compute road distance + duration between two addresses using Distance Matrix.
 * Returns { distanceMeters, durationSeconds } or throws.
 */
export const computeRouteKm = (origin, destination) => new Promise((resolve, reject) => {
  // Safety timeout — uneori Distance Matrix API nu invocă callback dacă cheia
  // nu are API-ul activat sau dacă există probleme de quota → evităm loading infinit.
  const timer = setTimeout(() => reject(new Error("TIMEOUT — Distance Matrix API nu răspunde (verifică dacă API-ul e activat în Google Cloud)")), 15000);
  if (!window.google || !window.google.maps) {
    clearTimeout(timer);
    return reject(new Error("Google Maps not ready"));
  }
  let service;
  try {
    service = new window.google.maps.DistanceMatrixService();
  } catch (e) {
    clearTimeout(timer);
    return reject(new Error("REQUEST_DENIED — Distance Matrix API nu este disponibil"));
  }
  service.getDistanceMatrix({
    origins: [origin],
    destinations: [destination],
    travelMode: window.google.maps.TravelMode.DRIVING,
    unitSystem: window.google.maps.UnitSystem.METRIC,
    region: "RO",
  }, (response, status) => {
    clearTimeout(timer);
    if (status !== "OK") return reject(new Error(`Distance Matrix: ${status}`));
    const row = response?.rows?.[0]?.elements?.[0];
    if (!row || row.status !== "OK") return reject(new Error(`Element: ${row?.status || "no result"}`));
    resolve({
      distanceMeters: row.distance.value,
      distanceText: row.distance.text,
      durationSeconds: row.duration.value,
      durationText: row.duration.text,
    });
  });
});
